import type { N8NServiceSchema } from "@/types/entities/n8n";
import { cn } from "@/utils/theme";
import { Body, Button, Heading, Icon } from "@elaniintc/goes-design-system";
import InputType from "./InputType";
import type { NodeVariable } from "../../types";
import { FormProvider, useForm, type UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  N8NDefaultValuesBuilder,
  N8NSchemaBuilder,
} from "../schemas/schema-builder";
import { useImperativeHandle, useMemo, type RefObject } from "react";
import type { NodeData } from "@/workflow_api/types/workflow";
interface Props {
  isLoading?: boolean;
  selectedService: string;
  data: N8NServiceSchema;
  error: boolean;
  retry?: () => void;
  ref: RefObject<UseFormReturn | null>;
  defaultData: NodeData["inputs"];
  variables: NodeVariable[];
  isLoadingVariables?: boolean;
}

const ServiceInputs = ({
  selectedService,
  isLoading,
  error,
  data,
  retry,
  ref,
  defaultData,
  variables,
  isLoadingVariables,
}: Props) => {
  const formSchema = useMemo(() => {
    if (!data) return;
    return N8NSchemaBuilder(data?.sections || []);
  }, [data]);

  const methods = useForm({
    ...(formSchema
      ? {
          resolver: zodResolver(formSchema),
          defaultValues: N8NDefaultValuesBuilder(
            data?.sections || [],
            defaultData,
          ),
        }
      : {}),
    mode: "onChange",
  });
  console.log(methods.formState.errors, methods.getValues());

  useImperativeHandle(ref, () => {
    return {
      getValues: () => methods.getValues(),
      trigger: () => methods.trigger(),
    } as UseFormReturn;
  }, [methods]);

  if (isLoading) {
    return (
      <div
        className={cn(
          "border-t border-neutral-quiet w-full px-4 py-6",
          "border-b flex flex-col justify-center items-center gap-3",
        )}
      >
        <Icon
          name="spinner-third"
          className="animate-spin text-on-disabled mx-auto text-3xl"
          type="far"
        />
        <p className="text-sm font-semibold leading-2xl text-on-disabled">
          Extrayendo campos...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={cn(
          "w-full text-center flex flex-col gap-2",
          "border-t border-neutral-quiet w-full px-4 py-6",
          "border-b flex flex-col justify-center items-center gap-3",
        )}
      >
        <Heading variant="h6">No se pudieron cargar los campos</Heading>
        <Body className="text-center">
          No se pudo establecer la conexión con el flujo N8N para extraer los
          campos requeridos. Por favor, inténtelo de nuevo.
        </Body>
        <Button
          className="pt-2.5 w-full"
          variant="quiet"
          type="button"
          onClick={retry}
        >
          Volver a intentar
        </Button>
      </div>
    );
  }

  return (
    <div className={cn("border-t border-neutral-quiet w-full px-4 py-6")}>
      <FormProvider {...methods} key={selectedService}>
        {data.sections.map((section, index) => (
          <Section
            key={`${section.title}-${index}`}
            data={section}
            service={selectedService}
            sectionId={index}
            nodeVariables={variables}
            isLoadingVariables={isLoadingVariables}
          />
        ))}
      </FormProvider>
    </div>
  );
};

export default ServiceInputs;

const Section = ({
  data,
  service,
  sectionId,
  nodeVariables,
  isLoadingVariables,
}: {
  service: string;
  sectionId: number;
  data: N8NServiceSchema["sections"][number];
  nodeVariables: NodeVariable[];
  isLoadingVariables?: boolean;
}) => {
  return (
    <div className="flex flex-col gap-2">
      <Heading variant="h6">{data.title}</Heading>
      <div className="w-full flex flex-col gap-2">
        {Object.entries(data.inputs).map(([inputName, inputData], index) => (
          <InputType
            key={`${service}-${inputName}-${index}-${sectionId}`}
            data={inputData}
            variableName={inputName}
            nodeVariables={nodeVariables}
            isLoadingVariables={isLoadingVariables}
          />
        ))}
      </div>
    </div>
  );
};
